import { HttpStatusCode } from "axios";
import { StyledEngineProvider, ThemeProvider, createTheme, responsiveFontSizes } from "@mui/material/styles";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import * as Sentry from "@sentry/react";
import ErrorPage from "_shared/_errors/_views/error-page";
import { SystemNotificationBannerProvider } from "_shared/_providers/banner/system-notification-banner-provider";
import ConfirmDialogProvider from "_shared/_providers/dialogs/confirm/confirm-dialog";
import DialogProvider from "_shared/_providers/dialogs/dialog";
import ImagePreviewDialogProvider from "_shared/_providers/dialogs/image-preview/image-preview-dialog";
import AppSnackbarProvider from "_shared/_providers/snackbars/app-snackbar-provider";
import attorneyHubStore from "_shared/_stores/redux-store";
import { enableInertModalHiding } from "_shared/_utilities/a11y/inert-modal-hiding";
import { ENVIRONMENT, IS_DEV, IS_E2E } from "_shared/_utilities/meta";
import Dashboard from "_shared/_views/dashboards/dashboard";
import dashboardLoader from "_shared/_views/dashboards/dashboard-loader";
import invitationLoader from "_shared/_views/loaders/invitation";
import marketplaceLoader from "_shared/_views/loaders/marketplace";
import marketplaceProfileLoader from "_shared/_views/loaders/marketplace-profile";
import marketplaceResultProfileLoader from "_shared/_views/loaders/marketplace-result-profile";
import networkLoader from "_shared/_views/loaders/network";
import rootLoader from "_shared/_views/loaders/root";
import superUserLoader from "_shared/_views/loaders/super-users";
import teamsLoader, { teamsListLoader } from "_shared/_views/loaders/teams";
import Main from "_shared/_views/main";
import LegalDocumentPage from "_shared/_views/legal/legal-document-page";
import Minimal from "_shared/_views/minimal/minimal";
import RouteAccessGuard from "_shared/_views/route-access-guard";
import Calendar from "calendar/_views/calendar";
import Teams from "_shared/teams/teams-page";
import DocumentsPage from "documents/_views/pages/documents-page";
import { contractsRedirectLoader, documentListLoader } from "documents/_views/pages/loaders/document-list-loader";
import "./index.css";
import EntityAdministration from "legal-entities/administrators/_views/administration-page";
import PermissionsHomeRoute from "permissions/_views/home/permissions-home-route";
import PermissionsShell from "permissions/_views/permissions-shell";
import PersonPermissionsPage from "permissions/_views/people/person-permissions-page";
import KindPermissionsPage from "permissions/_views/kinds/kind-permissions-page";
import RecordPermissionsPage from "permissions/_views/records/record-permissions-page";
import TemplatePage from "permissions/_views/templates/template-page";
import TemplatesPage from "permissions/_views/templates/templates-page";
import AccessRequestsPage from "permissions/access-requests/_views/access-requests-page";
import entityAdministratorLoader from "legal-entities/administrators/_views/administrator-loader";
import EntityAgreementPage from "legal-entities/agreements/_views/entity-agreement-page";
import UserEntityMemberInvitationAcceptancePage from "legal-entities/invitations/member-invitations/email-based/_views/user-entity-member-invitation-acceptance-page";
import OpenEntityMemberInvitationPage from "legal-entities/invitations/member-invitations/open/_views/open-entity-member-invitation-acceptance-page";
import EntityOfficerInvitationPage from "legal-entities/invitations/officer-invitations/_views/entity-officer-invitation-page";
import TeamLeaderRosterPage from "legal-entities/invitations/bulk/_views/team-leader-roster-page";
import TeamMemberRosterPage from "legal-entities/invitations/bulk/_views/team-member-roster-page";
import FirmOnboardingStatusPage from "legal-entities/vendor-rollouts/_views/firm-onboarding-status-page";
import {
  FIRM_ONBOARDING_DEFAULTS_PATH,
  FirmOnboardingTab,
} from "legal-entities/vendor-rollouts/firm-onboarding-routes";
import MarketplacePage from "marketplace/_views/marketplace-page";
import ManageProfiles from "marketplace/_views/profiles/profiles-page";
import Bookmarks from "marketplace/bookmarks/_views/bookmarks-page";
import bookmarksLoader from "marketplace/bookmarks/_views/loaders/bookmarks-loader";
import OpenSavedSearchPage from "marketplace/saved-searches/_views/open-saved-search-page";
import SavedSearchesPage from "marketplace/saved-searches/_views/saved-searches-page";
import TeamInvitationPage from "marketplace/teams/invitations/_views/team-invitation-page";
import CommunicationsPage from "notifications/_views/communications-page";
import ConversationDestinationPage from "messaging/conversations/_views/conversation-destination-page";
import communicationsLoader from "notifications/_views/loader/communications-loader";
import React from "react";
import { createRoot, type Root } from "react-dom/client";
import { Provider as ReduxProvider } from "react-redux";
import { createBrowserRouter, Navigate, Outlet, RouterProvider } from "react-router";

import { RequireSession, SessionProvider } from "users/__session/session-context";
import LoginPage from "users/auth/_views/login-page";
import Network from "users/networking/_views/network-page";
import SuperUserDashboard from "users/super-users/_views/super-user-administration-panel";
import FeeSchedulesPage from "work/fee-schedule-templates/_views/pages/fee-schedules-page";
import {
  activeFeeScheduleLoader,
  archivedFeeScheduleLoader,
  feeScheduleListLoader,
  templateFeeScheduleLoader,
} from "work/fee-schedule-templates/_views/pages/loaders/fee-schedule-loader";
import ActiveProposalPage from "work/proposals/_views/active/active-proposal-page";
import ArchivedProposalPage from "work/proposals/_views/archive/archived-proposal-page";
import DraftProposalPage from "work/proposals/_views/drafts/draft-proposal-page";
import {
  activeProposalLoader,
  archivedProposalLoader,
  draftProposalLoader,
  proposalListLoader,
  withDefaultTab,
} from "work/proposals/_views/loaders/proposal-loader";
import ProposalsPage from "work/proposals/_views/proposals-page";
import ProposalComparisonPage from "work/proposals/comparison/_views/proposal-comparison-page";
import TeamTemplatePage from "work/team-templates/_views/team-template-page";
import ViewWorkAgreementPage from "work/work-agreements/_views/view/view-work-agreement-page";
import {
  activeWorkAgreementLoader,
  archivedWorkAgreementLoader,
  workAgreementListLoader,
} from "work/work-agreements/_views/loaders/work-agreement-loader";
import WorkAgreementsPage from "work/work-agreements/_views/work-agreements-page";
import { policyReplacementRouteTree } from "work/policy-replacements/_views/policy-replacement-route-tree";
import FeeScheduleTemplatePage from "_shared/fee-schedules/categories/_views/templates/fee-schedule-template-page";
import HelpPage from "_shared/_views/help/help-page";
import SettingsPage from "users/settings/_views/settings-page";
import KnowledgeBasePage from "knowledge-base/_views/knowledge-base-page";
import { TutorialProvider } from "_shared/tutorials/tutorial-provider";
import { TableColumnPreferencesProvider } from "_shared/_views/tables/table-column-preferences-provider";
import { PanelLayoutPreferencesProvider } from "_shared/_views/panels/panel-layout-preferences-provider";
import { useSessionRecordingFollowsSignIn } from "_shared/session-recording/use-session-recording-follows-sign-in";
import { applySessionRecordingPrivacy } from "_shared/session-recording/session-recording-privacy";

declare global {
  interface Window {
    __attorneyHubBootstrap?: {
      fullStoryInitialized: boolean;
      fullStoryEnabled?: boolean;
      fullStoryCapturing?: boolean;
      sentryInitialized: boolean;
      root?: Root;
    };
  }
}

const bootstrapState = (window.__attorneyHubBootstrap ??= {
  fullStoryInitialized: false,
  sentryInitialized: false,
  root: undefined,
});
if (IS_E2E) document.documentElement.classList.add("e2e");

// FullStory in devMode does nothing except announce, on every boot, that it will not record. Skip it
// locally rather than paying for a console warning that carries no information.
//
// Enabled is not started. FullStory loads only once someone is signed in and this device has
// accepted, so nothing is fetched from it, and none of its cookies set, before then (#5168).
//
// The privacy rules go on first, because they have to be watching before FullStory's own observer
// exists: images excluded, typed values masked, the interface's own words unmasked. Everything else
// is masked by the `fs-mask` class on `<body>` in index.html.
if (!IS_E2E && !IS_DEV && !bootstrapState.fullStoryEnabled) {
  applySessionRecordingPrivacy();
  bootstrapState.fullStoryEnabled = true;
}

// Sentry is not behind the session recording consent. It reports errors and records no session —
// no Replay integration is configured — and it sets no cookies. Adding Replay would change that:
// Replay must then wait for the same consent as FullStory. Decided on #5168.

if (!IS_E2E && !bootstrapState.sentryInitialized) {
  Sentry.init({
    dsn: "https://9b4b0f7cb45a485ea5dcef37b8da09d7@o1250895.ingest.sentry.io/6415866",
    enabled: !IS_DEV,
    environment: ENVIRONMENT,
    tracesSampleRate: 1,
  });
  bootstrapState.sentryInitialized = true;
}

// Backport MUI's `inert`-based background hiding so stacked modals/popovers
// never leave focus inside an `aria-hidden` ancestor (Chrome a11y violation).
enableInertModalHiding();

let theme = createTheme({
  components: {
    /**
     * Every spinner keeps a fixed arc and only rotates. The default grows and shrinks its arc through
     * `stroke-dasharray`, which the main thread animates — so while a page is busy rendering, the arc
     * stalls at its first frame, a 1px dash, and a table's loading overlay or a tab's count reads as a
     * stray blue sliver (#5907). Rotation is a transform the compositor keeps running regardless. This
     * is MUI's own advice for heavy load: https://mui.com/material-ui/react-progress/#limitations
     */
    MuiCircularProgress: {
      defaultProps: { disableShrink: true },
    },
  },
});
theme = responsiveFontSizes(theme);

const RequireSessionLayout = () => (
  <RequireSession>
    <Outlet />
  </RequireSession>
);

// At the root of every route, so FullStory and the footer's Cookie preferences follow the sign-in
// on every page, inside the shell or not.
const DialogLayout = () => {
  useSessionRecordingFollowsSignIn();

  return (
    <DialogProvider>
      <TutorialProvider>
        <TableColumnPreferencesProvider>
          <PanelLayoutPreferencesProvider>
            <Outlet />
          </PanelLayoutPreferencesProvider>
        </TableColumnPreferencesProvider>
      </TutorialProvider>
    </DialogProvider>
  );
};

const routes = [
  {
    path: "entity-officer-invitation/:invitationId",
    element: <EntityOfficerInvitationPage />,
    errorElement: <ErrorPage />,
    loader: invitationLoader,
  },
  {
    path: "entity-member-invitation/:invitationId",
    element: <OpenEntityMemberInvitationPage />,
    errorElement: <ErrorPage />,
    loader: invitationLoader,
  },
  {
    path: "user-member-invitation/:invitationId",
    element: <UserEntityMemberInvitationAcceptancePage />,
    errorElement: <ErrorPage />,
    loader: invitationLoader,
  },
  {
    path: "login",
    element: <LoginPage />,
    errorElement: <ErrorPage />,
  },
  {
    // Outside the session-guarded tree: the consent banner links the Privacy Policy before anyone
    // has signed in.
    element: <Minimal />,
    errorElement: <ErrorPage />,
    children: [
      {
        path: "privacy-policy",
        element: <LegalDocumentPage title="Privacy Policy" />,
      },
      {
        path: "terms-of-use",
        element: <LegalDocumentPage title="Terms of Use" />,
      },
    ],
  },
  {
    path: "/",
    element: <RouteAccessGuard><Main /></RouteAccessGuard>,
    errorElement: <ErrorPage />,
    loader: rootLoader,
    children: [
      {
        element: <RequireSessionLayout />,
        errorElement: <ErrorPage />,
        children: [
          {
            path: "dashboard",
            element: <Dashboard />,
            loader: dashboardLoader,
          },

          {
            path: "communications",
            element: <CommunicationsPage />,
            loader: communicationsLoader,
          },
          {
            path: "communications/:pageTab",
            element: <CommunicationsPage />,
            loader: communicationsLoader,
          },
          {
            path: "communications/:pageTab/:action",
            element: <CommunicationsPage />,
            loader: communicationsLoader,
          },
          {
            path: "communications/:pageTab/:action/:id",
            element: <CommunicationsPage />,
            loader: communicationsLoader,
          },

          {
            // Where a message notification about an agreement's or a proposal's conversation lands:
            // it sends the reader on to the page that opens on that conversation (#5826).
            path: "conversations/:forumId",
            element: <ConversationDestinationPage />,
          },

          {
            path: "documents",
            element: <DocumentsPage />,
          },
          {
            path: "documents/:pageTab",
            element: <DocumentsPage />,
            loader: documentListLoader,
          },
          // The page was Contracts until #6720; its links open the Documents list that holds their rows.
          {
            path: "contracts",
            loader: contractsRedirectLoader,
          },
          {
            path: "contracts/:pageTab",
            loader: contractsRedirectLoader,
          },

          {
            path: "work",
            children: [
              {
                index: true,
                element: <WorkAgreementsPage />,
              },
              {
                // The list has no tabs since #6716: each old tab redirects to the list or its chip.
                path: ":pageTab",
                element: <WorkAgreementsPage />,
                loader: workAgreementListLoader,
              },

              {
                path: "active/:action/:id",
                element: <ViewWorkAgreementPage />,
                loader: withDefaultTab("details", activeWorkAgreementLoader),
              },
              {
                path: "active/:action/:id/:pageTab",
                element: <ViewWorkAgreementPage />,
                loader: activeWorkAgreementLoader,
              },

              {
                path: "archived/:action/:id",
                element: <ViewWorkAgreementPage />,
                loader: withDefaultTab("details", archivedWorkAgreementLoader),
              },
              {
                path: "archived/:action/:id/:pageTab",
                element: <ViewWorkAgreementPage />,
                loader: archivedWorkAgreementLoader,
              },
            ],
          },

          policyReplacementRouteTree,

          {
            path: "proposals",
            children: [
              {
                index: true,
                element: <ProposalsPage />,
                loader: proposalListLoader,
              },
              {
                path: ":pageTab",
                element: <ProposalsPage />,
                loader: proposalListLoader,
              },
              // The proposals one multi-firm send (#576) produced, side by side, for the client (#581).
              {
                path: "compare/:rfpId",
                element: <ProposalComparisonPage />,
              },

              {
                path: "drafts/create",
                element: <DraftProposalPage />,
                loader: withDefaultTab("details", draftProposalLoader),
              },
              {
                path: "drafts/create/:pageTab",
                element: <DraftProposalPage />,
                loader: draftProposalLoader,
              },

              {
                path: "drafts/:action/:id",
                element: <DraftProposalPage />,
                loader: withDefaultTab("details", draftProposalLoader),
              },
              {
                path: "drafts/:action/:id/:pageTab",
                element: <DraftProposalPage />,
                loader: draftProposalLoader,
              },

              {
                path: "active/:action/:id",
                element: <ActiveProposalPage />,
                loader: withDefaultTab("details", activeProposalLoader),
              },
              {
                path: "active/:action/:id/:pageTab",
                element: <ActiveProposalPage />,
                loader: activeProposalLoader,
              },

              {
                path: "archived/:action/:id",
                element: <ArchivedProposalPage />,
                loader: withDefaultTab("details", archivedProposalLoader),
              },
              {
                path: "archived/:action/:id/:pageTab",
                element: <ArchivedProposalPage />,
                loader: archivedProposalLoader,
              },
            ],
          },

          {
            // A plain visit opens the reader's own view of the page, else Teams (#6714, #6718).
            path: "teams",
            element: <Teams />,
          },
          {
            // Teams and Templates since #6718: each old tab redirects to the tab and chip it became.
            path: "teams/:pageTab",
            element: <Teams />,
            loader: teamsListLoader,
          },
          {
            path: "teams/templates/create",
            element: <TeamTemplatePage />,
            loader: teamsLoader,
          },
          {
            path: "teams/templates/:action/:id",
            element: <TeamTemplatePage />,
            loader: teamsLoader,
          },
          {
            path: "teams/archived/:action/:id",
            element: <TeamTemplatePage />,
            loader: teamsLoader,
          },

          {
            path: "fee-schedules",
            children: [
              {
                // A plain visit opens the reader's own view of the page, else Templates (#6714).
                index: true,
                element: <FeeSchedulesPage />,
              },
              {
                // Templates and In use since #6716: each old tab redirects to the tab or chip it became.
                path: ":pageTab",
                element: <FeeSchedulesPage />,
                loader: feeScheduleListLoader,
              },
              {
                path: "templates/create",
                element: <FeeScheduleTemplatePage />,
                loader: templateFeeScheduleLoader,
              },
              {
                path: "templates/:action/:id",
                element: <FeeScheduleTemplatePage />,
                loader: templateFeeScheduleLoader,
              },
              {
                path: "active/:action/:id",
                element: <FeeScheduleTemplatePage />,
                loader: activeFeeScheduleLoader,
              },
              {
                path: "archived/:action/:id",
                element: <FeeScheduleTemplatePage />,
                loader: archivedFeeScheduleLoader,
              },
            ],
          },

          {
            path: "marketplace/vendor-team-invitation/:invitationId",
            element: <TeamInvitationPage />,
          },

          {
            path: "marketplace/profiles",
            element: <ManageProfiles />,
            loader: marketplaceProfileLoader,
          },
          {
            path: "marketplace/profiles/:pageTab",
            element: <ManageProfiles />,
            loader: marketplaceProfileLoader,
          },
          {
            path: "marketplace/profiles/:pageTab/:action",
            element: <ManageProfiles />,
            loader: marketplaceProfileLoader,
          },
          {
            path: "marketplace/profiles/:pageTab/:action/:id",
            element: <ManageProfiles />,
            loader: marketplaceProfileLoader,
          },

          {
            path: "marketplace/bookmarks",
            element: <Bookmarks />,
            loader: bookmarksLoader,
          },
          {
            path: "marketplace/bookmarks/:pageTab",
            element: <Bookmarks />,
            loader: bookmarksLoader,
          },

          {
            path: "marketplace/saved-searches",
            element: <SavedSearchesPage />,
          },
          {
            // Where a saved search's new-match notification leads (#2411).
            path: "marketplace/saved-searches/:savedSearchId",
            element: <OpenSavedSearchPage />,
          },

          {
            path: "marketplace",
            element: <MarketplacePage />,
            loader: marketplaceLoader,
          },
          {
            path: "marketplace/:pageTab",
            element: <MarketplacePage />,
            loader: marketplaceResultProfileLoader,
          },
          {
            path: "marketplace/:pageTab/:id",
            element: <MarketplacePage />,
            loader: marketplaceResultProfileLoader,
          },

          {
            path: "network",
            element: <Network />,
            loader: networkLoader,
          },
          {
            path: "network/:pageTab",
            element: <Network />,
            loader: networkLoader,
          },
          {
            path: "network/:pageTab/:action",
            element: <Network />,
            loader: networkLoader,
          },
          {
            path: "network/:pageTab/:action/:id",
            element: <Network />,
            loader: networkLoader,
          },

          {
            path: "calendar",
            element: <Calendar />,
          },

          {
            path: "organization",
            element: <EntityAdministration />,
            loader: entityAdministratorLoader,
          },
          {
            path: "organization/:pageTab",
            element: <EntityAdministration />,
            loader: entityAdministratorLoader,
          },
          {
            path: "organization/:pageTab/:action",
            element: <EntityAdministration />,
            loader: entityAdministratorLoader,
          },
          {
            path: "organization/:pageTab/:action/:id",
            element: <EntityAdministration />,
            loader: entityAdministratorLoader,
          },
          {
            // One shell owns the session-permissions load; each route below gates itself (#6168).
            path: "permissions",
            element: <PermissionsShell />,
            loader: entityAdministratorLoader,
            children: [
              { index: true, element: <PermissionsHomeRoute /> },
              {
                path: "people/:userId",
                element: <PersonPermissionsPage />,
              },
              {
                path: "kinds/:slug",
                element: <KindPermissionsPage />,
              },
              {
                path: "records/:domain/:recordId",
                element: <RecordPermissionsPage />,
              },
              {
                path: "templates",
                element: <TemplatesPage />,
              },
              {
                path: "templates/:templateId",
                element: <TemplatePage />,
              },
              {
                // Access members asked for, and the reader's own requests (#6596).
                path: "requests",
                element: <AccessRequestsPage />,
              },
            ],
          },
          {
            path: "firm-onboarding",
            children: [
              { index: true, element: <FirmOnboardingStatusPage /> },
              {
                path: "defaults",
                element: <FirmOnboardingStatusPage tab={FirmOnboardingTab.Defaults} />,
              },
            ],
          },
          {
            // Moved to the Firm Onboarding page's Defaults tab (#6261). Kept so a bookmark still lands.
            path: "client-proposal-templates",
            element: <Navigate to={FIRM_ONBOARDING_DEFAULTS_PATH} replace />,
          },
          {
            path: "invite-colleagues",
            element: <TeamMemberRosterPage />,
          },
          {
            // The same page, named by the firm whose roster it is. This is where the prompt raised
            // on the Attorney Hub proposal's hire lands: notification routes substitute a topic id
            // into the path, and this notification's topic is the firm.
            path: "invite-colleagues/:entityId",
            element: <TeamMemberRosterPage />,
          },
          {
            path: "invite-team-leaders",
            element: <TeamLeaderRosterPage />,
          },
          {
            path: "super-user",
            element: <SuperUserDashboard />,
            loader: superUserLoader,
          },
          {
            path: "super-user/:pageTab",
            element: <SuperUserDashboard />,
            loader: superUserLoader,
          },
          {
            path: "super-user/:pageTab/:profileType/:id",
            element: <SuperUserDashboard />,
            loader: superUserLoader,
          },

          {
            path: "knowledge-base",
            element: <KnowledgeBasePage />,
          },
          {
            // Ahead of the :slug route, and "attorney-hub" is a reserved slug on the server, so an
            // article can never be created that this address would otherwise shadow.
            path: "knowledge-base/attorney-hub",
            element: <KnowledgeBasePage publicLibraryOnly />,
          },
          {
            // Attorney Hub's articles have addresses of their own. Without these, opening one had
            // nowhere to go but the entity route, which reads a different knowledge base entirely.
            path: "knowledge-base/attorney-hub/:slug",
            element: <KnowledgeBasePage publicLibraryOnly />,
          },
          {
            path: "knowledge-base/attorney-hub/:slug/edit",
            element: <KnowledgeBasePage publicLibraryOnly />,
          },
          {
            path: "knowledge-base/:slug",
            element: <KnowledgeBasePage />,
          },
          {
            path: "knowledge-base/:slug/edit",
            element: <KnowledgeBasePage />,
          },
          {
            path: "help",
            element: <HelpPage />,
          },
          {
            path: "settings",
            element: <SettingsPage />,
          },
          {
            path: "entity-agreements/:id",
            element: <EntityAgreementPage />,
          },
        ],
      },
    ],
  },
  {
    path: "*",
    element: <ErrorPage error={{ status: HttpStatusCode.NotFound, statusText: "Page Not Found" }} />,
  },
];

const router = createBrowserRouter([
  {
    element: <DialogLayout />,
    children: routes,
  },
]);

const container = document.getElementById("root");
if (!container) {
  throw new Error("Root element not found");
}

const root = (bootstrapState.root ??= createRoot(container));
const application = (
  <ReduxProvider store={attorneyHubStore}>
    <StyledEngineProvider injectFirst>
      <ThemeProvider theme={theme}>
        <LocalizationProvider dateAdapter={AdapterMoment}>
          <SystemNotificationBannerProvider>
            <AppSnackbarProvider>
              <SessionProvider>
                <ConfirmDialogProvider>
                  <ImagePreviewDialogProvider>
                    <RouterProvider router={router} />
                  </ImagePreviewDialogProvider>
                </ConfirmDialogProvider>
              </SessionProvider>
            </AppSnackbarProvider>
          </SystemNotificationBannerProvider>
        </LocalizationProvider>
      </ThemeProvider>
    </StyledEngineProvider>
  </ReduxProvider>
);
root.render(IS_E2E ? application : <React.StrictMode>{application}</React.StrictMode>);
